import type { Device, MountingPointOrientation } from "types/device.types"
import type { TowerPositionVariant, TowerViewStyle } from "types/devicePosition.types"

import sentriV4TowerPoleS1 from "images/devices/position/dynamic/sentri-v4-tower-pole-s1.png"
import sentriV4TowerPoleS2 from "images/devices/position/dynamic/sentri-v4-tower-pole-s2.png"
import sentriV4TowerS1 from "images/devices/position/dynamic/sentri-v4-tower-s1.png"
import sentriV4TowerS2 from "images/devices/position/dynamic/sentri-v4-tower-s2.png"
import sentriV2V3TowerS1 from "images/devices/position/dynamic/sentri-v2-v3-tower-s1.png"
import sentriV2V3TowerS2 from "images/devices/position/dynamic/sentri-v2-v3-tower-s2.png"
import mastTowerSide from "images/towers/mast-tower-side.png"
import mastTowerFront from "images/towers/mast-tower-front.png"
import devicePointFront from "images/devices/position/dynamic/device-point-front.png"
import devicePointRight from "images/devices/position/dynamic/device-point-right.png"
import facingFrontLeftSide from "images/devices/position/dynamic/facing-front-left-side-view.png"
import facingFrontFrontal from "images/devices/position/dynamic/facing-front-frontal-view.png"
import facingLeftLeftSide from "images/devices/position/dynamic/facing-left-left-side-view.png"
import facingLeftFrontal from "images/devices/position/dynamic/facing-left-frontal-view.png"
import facingRightLeftSide from "images/devices/position/dynamic/facing-right-left-side-view.png"
import facingRightFrontal from "images/devices/position/dynamic/facing-right-frontal-view.png"
import facingBackLeftSide from "images/devices/position/dynamic/facing-back-left-view.png"
import facingBackFrontal from "images/devices/position/dynamic/facing-back-frontal-view.png"
import poleFacingFrontLeftSide from "images/devices/position/dynamic/pole-360/pole-facing-front-left-side-view.png"
import poleFacingFrontFrontal from "images/devices/position/dynamic/pole-360/pole-facing-front-frontal-view.png"
import poleFacingLeftLeftSide from "images/devices/position/dynamic/pole-360/pole-facing-left-left-side-view.png"
import poleFacingLeftFrontal from "images/devices/position/dynamic/pole-360/pole-facing-left-frontal-view.png"
import poleFacingRightLeftSide from "images/devices/position/dynamic/pole-360/pole-facing-right-left-side-view.png"
import poleFacingRightFrontal from "images/devices/position/dynamic/pole-360/pole-facing-right-frontal-view.png"
import poleFacingBackLeftSide from "images/devices/position/dynamic/pole-360/pole-facing-back-left-view.png"
import poleFacingBackFrontal from "images/devices/position/dynamic/pole-360/pole-facing-back-frontal-view.png"
import { getDeviceVersionAndTarget, isVersionAboveOrEqual } from "./devices"

const TOOLTIP_KEY_PREFIX = "plots_descriptions.DEVICE_POSITION"

interface TowerPositionConfig {
  viewSrcs: { first: string; second: string }
  tooltipKey: string
  viewStyle: TowerViewStyle
}

type TowerVariantConfig = Omit<TowerPositionConfig, "viewStyle">

const TOWER_POSITION_CONFIG: Record<TowerPositionVariant, TowerVariantConfig> = {
  v2v3: {
    viewSrcs: { first: sentriV2V3TowerS1, second: sentriV2V3TowerS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_V2_V3`,
  },
  v4: {
    viewSrcs: { first: sentriV4TowerS1, second: sentriV4TowerS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_V4`,
  },
  "v4-legacy": {
    viewSrcs: { first: sentriV4TowerS1, second: sentriV4TowerS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_V4_LEGACY`,
  },
  "v4-pole": {
    viewSrcs: { first: sentriV4TowerPoleS1, second: sentriV4TowerPoleS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_HEALTH`,
  },
  "v4-pole-legacy": {
    viewSrcs: { first: sentriV4TowerPoleS1, second: sentriV4TowerPoleS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_POLE_V4`,
  },
  "v4-hva": {
    viewSrcs: { first: sentriV4TowerS1, second: sentriV4TowerS2 },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_HEALTH`,
  },
  "v4-mast": {
    viewSrcs: { first: mastTowerSide, second: mastTowerFront },
    tooltipKey: `${TOOLTIP_KEY_PREFIX}.LAST_POSITION_TOWER_HEALTH`,
  },
}

const TOWER_RELATIVE_VIEW_SRCS = { first: devicePointFront, second: devicePointRight }

const TOWER_RELATIVE_ORIENTATION_SRCS: Record<
  MountingPointOrientation,
  { first: string; second: string }
> = {
  facing_front: { first: facingFrontLeftSide, second: facingFrontFrontal },
  facing_left: { first: facingLeftLeftSide, second: facingLeftFrontal },
  facing_right: { first: facingRightLeftSide, second: facingRightFrontal },
  facing_back: { first: facingBackLeftSide, second: facingBackFrontal },
}

/** A pole is round, so it gets the same four orientations drawn on a pole shaft. */
const TOWER_RELATIVE_POLE_ORIENTATION_SRCS: Record<
  MountingPointOrientation,
  { first: string; second: string }
> = {
  facing_front: { first: poleFacingFrontLeftSide, second: poleFacingFrontFrontal },
  facing_left: { first: poleFacingLeftLeftSide, second: poleFacingLeftFrontal },
  facing_right: { first: poleFacingRightLeftSide, second: poleFacingRightFrontal },
  facing_back: { first: poleFacingBackLeftSide, second: poleFacingBackFrontal },
}

export const getTowerPositionVariant = (
  device: Device,
  towerType?: string,
  towerHealthEnabled = false,
): TowerPositionVariant => {
  const { version } = getDeviceVersionAndTarget(device)
  if (!isVersionAboveOrEqual(version, "4.0")) return "v2v3"
  if (towerType === "pole") return towerHealthEnabled ? "v4-pole" : "v4-pole-legacy"
  if (towerType === "steel_lattice_guyed_a_frame") return "v4-hva"
  if (towerType === "steel_lattice_guyed_mast") return "v4-mast"
  return towerHealthEnabled ? "v4" : "v4-legacy"
}

export const getTowerPositionConfig = (
  variant: TowerPositionVariant,
  towerHealthEnabled = false,
  orientation?: MountingPointOrientation | null,
): TowerPositionConfig => {
  const config = TOWER_POSITION_CONFIG[variant]
  if (towerHealthEnabled) {
    const isPole = variant === "v4-pole" || variant === "v4-pole-legacy"
    const orientationSrcs = isPole
      ? TOWER_RELATIVE_POLE_ORIENTATION_SRCS
      : TOWER_RELATIVE_ORIENTATION_SRCS
    // A tower device without an orientation never gets tilt telemetry, so this
    // falls back to the lattice section only to keep the card from going blank.
    const viewSrcs =
      (orientation && orientationSrcs[orientation]) ?? TOWER_RELATIVE_VIEW_SRCS
    return { ...config, viewSrcs, viewStyle: "tower-relative" }
  }
  return { ...config, viewStyle: variant }
}
